import { DESCRIPTION_MAX } from '@servicetim/shared';
import { Button, describe, ProblemBanner, Textarea, toast } from '@servicetim/ui/components';
import { LoaderCircle, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTicketContext } from './ticket-context';

/**
 * Description: rendered read view, textarea edit saved with `If-Match`.
 * TODO: render Markdown once a renderer is approved (no new runtime dependency in this bolt);
 * the read view keeps whitespace and line breaks.
 */
export function TicketDescription() {
  const { ticket, patch, patching } = useTicketContext();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ticket.description ?? '');
  const [error, setError] = useState<unknown>(null);

  const start = () => {
    setDraft(ticket.description ?? '');
    setError(null);
    setEditing(true);
  };
  const save = async () => {
    const next = draft.trim().length === 0 ? null : draft;
    if (next === ticket.description) {
      setEditing(false);
      return;
    }
    try {
      const saved = await patch({ description: next });
      if (saved) {
        toast.success('Description saved');
        setEditing(false);
      }
    } catch (e) {
      setError(e);
      toast.error(describe(e).title);
    }
  };

  return (
    <section aria-label="Description" className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-fg text-sm">Description</h2>
        {ticket.canEdit && !editing ? (
          <Button size="sm" variant="ghost" onClick={start}>
            <Pencil />
            Edit
          </Button>
        ) : null}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            aria-label="Description"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            maxLength={DESCRIPTION_MAX}
            autoFocus
          />
          {error ? <ProblemBanner error={error} size="sm" /> : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={patching}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={patching}>
              {patching ? <LoaderCircle className="animate-spin" /> : null}
              Save
            </Button>
          </div>
        </div>
      ) : ticket.description ? (
        <div className="whitespace-pre-wrap break-words rounded-md border border-border bg-surface-raised p-4 text-fg text-sm leading-relaxed">
          {ticket.description}
        </div>
      ) : (
        <button
          type="button"
          onClick={ticket.canEdit ? start : undefined}
          disabled={!ticket.canEdit}
          className="w-full rounded-md border border-border border-dashed p-4 text-left text-fg-muted text-sm hover:border-border-strong hover:text-fg-muted disabled:cursor-default disabled:hover:border-border disabled:hover:text-fg-muted"
        >
          {ticket.canEdit ? 'Add a description…' : 'No description.'}
        </button>
      )}
    </section>
  );
}
